import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useCurrentWeather } from '@/hooks/useWeather';
import { Cloud, CloudRain, Sun, Wind, CloudSnow, CloudDrizzle, ArrowUp } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

// Convert wind direction abbreviation to degrees for arrow rotation
// Wind direction indicates where wind comes FROM, so we add 180° to show where it blows TO
const getWindDirectionDegrees = (direction: string): number => {
  const directions: Record<string, number> = {
    'N': 180, 'NNE': 202.5, 'NE': 225, 'ENE': 247.5,
    'E': 270, 'ESE': 292.5, 'SE': 315, 'SSE': 337.5,
    'S': 0, 'SSW': 22.5, 'SW': 45, 'WSW': 67.5,
    'W': 90, 'WNW': 112.5, 'NW': 135, 'NNW': 157.5,
  };
  return directions[direction] ?? 0;
};

const getWeatherIcon = (condition: string) => {
  switch (condition) {
    case 'sunny':
      return <Sun size={64} color={Colors.primary} />;
    case 'cloudy':
      return <Cloud size={64} color={Colors.primary} />;
    case 'partly-cloudy':
      return <Cloud size={64} color={Colors.primary} />;
    case 'rainy':
      return <CloudRain size={64} color={Colors.primary} />;
    case 'snowy':
      return <CloudSnow size={64} color={Colors.primary} />;
    case 'drizzle':
      return <CloudDrizzle size={64} color={Colors.primary} />;
    default:
      return <Cloud size={64} color={Colors.primary} />;
  }
};

export default function WeatherPage() {
  const { language } = useAppLanguage();
  const weatherQuery = useCurrentWeather();

  if (weatherQuery.isLoading) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('weather', language)} />
        <div style={styles.content}>
          <div style={styles.loadingMessage}>Loading weather data...</div>
        </div>
      </DetailPageLayout>
    );
  }

  if (weatherQuery.isError) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('weather', language)} />
        <div style={styles.content}>
          <div style={styles.errorMessage}>
            Unable to load weather data. Please try again later.
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const data = weatherQuery.data;
  if (!data) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('weather', language)} />
        <div style={styles.content}>
          <div style={styles.errorMessage}>No weather data available</div>
        </div>
      </DetailPageLayout>
    );
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const forecastDays = data.forecast.slice(0, 5);

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('weather', language)} />

      <div style={styles.content}>
        <div style={styles.currentWeather}>
          <div style={styles.tempDisplay}>
            {getWeatherIcon(data.condition)}
            <div style={styles.tempValue}>{Math.round(data.temperature)}°C</div>
            <div style={styles.tempCondition}>{data.weatherDescription}</div>
          </div>

          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <Wind size={18} color={Colors.primary} />
              <div style={styles.detailLabel}>Wind</div>
              <div style={styles.detailValue}>{Math.round(data.windSpeed)} m/s</div>
            </div>
            <div style={styles.detailItem}>
              <ArrowUp
                size={24}
                color={Colors.primary}
                style={{
                  transform: `rotate(${getWindDirectionDegrees(data.windDirection)}deg)`,
                  transition: 'transform 0.3s ease'
                }}
              />
              <div style={styles.detailLabel}>Direction</div>
              <div style={styles.detailValue}>{data.windDirection}</div>
            </div>
          </div>
        </div>

        <div style={styles.forecast}>
          <h2 style={styles.forecastTitle}>5-Day Forecast</h2>
          {forecastDays.map((item) => {
            const date = new Date(item.date);
            const dayName = dayNames[date.getDay()];
            return (
              <div key={item.date} style={styles.forecastItem}>
                <div style={styles.forecastDay}>{dayName}</div>
                {getWeatherIcon(item.condition)}
                <div style={styles.forecastTemp}>
                  {Math.round(item.temperatureMax)}°C / {Math.round(item.temperatureMin)}°C
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ height: '40px' }} />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  loadingMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
    fontSize: '16px',
  } as React.CSSProperties,

  errorMessage: {
    padding: '24px',
    textAlign: 'center' as const,
    color: '#d32f2f',
    fontSize: '16px',
  } as React.CSSProperties,

  currentWeather: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  tempDisplay: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    marginBottom: '16px',
  } as React.CSSProperties,

  tempValue: {
    fontSize: '40px',
    fontWeight: '700',
    color: Colors.primary,
    margin: '6px 0',
  } as React.CSSProperties,

  tempCondition: {
    fontSize: '14px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  } as React.CSSProperties,

  detailItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '12px',
    backgroundColor: Colors.primaryLight,
    borderRadius: '10px',
  } as React.CSSProperties,

  detailLabel: {
    fontSize: '11px',
    color: Colors.textSecondary,
    marginTop: '6px',
  } as React.CSSProperties,

  detailValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: Colors.text,
  } as React.CSSProperties,

  forecast: {
    marginTop: '24px',
  } as React.CSSProperties,

  forecastTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: Colors.text,
    marginBottom: '16px',
    margin: 0,
  } as React.CSSProperties,

  forecastItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    marginBottom: '8px',
    gap: '12px',
  } as React.CSSProperties,

  forecastDay: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.text,
    width: '50px',
  } as React.CSSProperties,

  forecastTemp: {
    fontSize: '14px',
    fontWeight: '700',
    color: Colors.primary,
    marginLeft: 'auto',
  } as React.CSSProperties,

  forecastCondition: {
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,
} as const;
